import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Calculator, TrendingUp } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";

const SITE = "https://hireds.in";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = [
  { v: "gen", label: "General / UR" },
  { v: "obc", label: "OBC-NCL" },
  { v: "ews", label: "EWS" },
  { v: "sc", label: "SC" },
  { v: "st", label: "ST" },
];

function verdict(yourMarks, exam, cat) {
  const rows = exam.rows;
  const latest = rows[rows.length - 1];
  const cutoff = latest[cat] ?? latest.gen;
  const diff = yourMarks - cutoff;
  const oldRows = rows.slice(0, -1);
  const oldAvg = oldRows.reduce((acc, r) => acc + (r[cat] ?? r.gen), 0) / oldRows.length;

  if (diff >= 8) return { tier: "safe", label: "Safe — likely to clear", colour: "green", detail: `You are ${diff.toFixed(1)} marks above the latest ${exam.exam} ${cat.toUpperCase()} cutoff of ${cutoff}. Historically, a ${diff.toFixed(0)}-mark buffer has converted 95%+ of times.` };
  if (diff >= 2) return { tier: "likely", label: "Likely to clear", colour: "blue", detail: `You are ${diff.toFixed(1)} marks above the latest cutoff (${cutoff}). 70-85% candidates with this buffer have cleared in past cycles. Old 2-year avg: ${oldAvg.toFixed(1)}.` };
  if (diff >= -3) return { tier: "borderline", label: "Borderline — depends on normalisation", colour: "amber", detail: `You are within ${Math.abs(diff).toFixed(1)} marks of the latest cutoff (${cutoff}). Final cutoff may shift ±2-3 due to normalisation; the call is too close.` };
  return { tier: "risky", label: "Below cutoff — unlikely to clear", colour: "red", detail: `You are ${Math.abs(diff).toFixed(1)} marks below the latest cutoff of ${cutoff}. Even at historical 2-year average (${oldAvg.toFixed(1)}), your score falls short.` };
}

export default function RankPredictorPage() {
  const [cutoffs, setCutoffs] = useState([]);
  const [examSlug, setExamSlug] = useState("");
  const [cat, setCat] = useState("gen");
  const [marks, setMarks] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const oldTitle = document.title;
    document.title = "Govt Exam Rank / Cut-off Predictor 2026 — SSC, RRB, IBPS, UPSC | hireds.in";
    const md = document.querySelector('meta[name="description"]');
    const oldMd = md ? md.getAttribute("content") : null;
    if (md) md.setAttribute("content", "Enter your marks → instantly see if you cleared SSC CGL, RRB NTPC, IBPS PO, UPSC Prelims based on last 3 years of category-wise cutoff data. Free predictor by hireds.in.");
    const canon = document.querySelector('link[rel="canonical"]');
    const oldCanon = canon ? canon.getAttribute("href") : null;
    if (canon) canon.setAttribute("href", `${SITE}/tools/rank-predictor`);
    return () => {
      document.title = oldTitle;
      if (md && oldMd != null) md.setAttribute("content", oldMd);
      if (canon && oldCanon != null) canon.setAttribute("href", oldCanon);
    };
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/cutoffs`);
        if (cancel) return;
        const exams = Array.isArray(data.exams) ? data.exams : [];
        setCutoffs(exams);
        if (exams.length) setExamSlug(exams[0].exam_slug);
      } catch (e) {
        setError("Unable to load cut-off data right now. Refresh in a moment.");
      }
    })();
    return () => { cancel = true; };
  }, []);

  const exam = useMemo(() => cutoffs.find((e) => e.exam_slug === examSlug), [cutoffs, examSlug]);
  const numericMarks = parseFloat(marks);
  const valid = exam && !isNaN(numericMarks) && numericMarks >= 0 && numericMarks <= exam.out_of;
  const result = valid ? verdict(numericMarks, exam, cat) : null;

  const colourMap = {
    safe: "bg-green-50 border-green-300 text-green-900",
    likely: "bg-blue-50 border-blue-300 text-blue-900",
    borderline: "bg-amber-50 border-amber-300 text-amber-900",
    risky: "bg-red-50 border-red-300 text-red-900",
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6" data-testid="rank-predictor">
      <Breadcrumbs items={[
        { name: "Home", url: "/" },
        { name: "Tools", url: "/tools" },
        { name: "Rank Predictor", url: "/tools/rank-predictor" },
      ]} />
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">Free Tool</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Govt Exam Cut-off &amp; Rank Predictor 2026
        </h1>
        <p className="text-base text-slate-700 mt-2 leading-relaxed">
          Enter your expected marks → instantly see whether you would have cleared the latest cycle. Verdict is calibrated against actual category-wise cutoffs from the last 3 years.
        </p>
      </header>

      <section className="bg-white border border-slate-200 rounded-xl p-5 md:p-7">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-orange-100 text-orange-700">
            <Calculator className="w-4 h-4" aria-hidden="true" />
          </span>
          <h2 className="text-xl md:text-2xl font-extrabold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            Predict your verdict
          </h2>
        </div>

        {error && <p className="text-sm text-red-600 mb-3" data-testid="rank-error">{error}</p>}

        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-xs font-bold text-slate-700 mb-1 inline-block">Exam</span>
            <select
              value={examSlug}
              onChange={(e) => setExamSlug(e.target.value)}
              data-testid="rank-exam"
              className="w-full min-h-11 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            >
              {cutoffs.map((e) => (
                <option key={e.exam_slug} value={e.exam_slug}>{e.exam}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-bold text-slate-700 mb-1 inline-block">Category</span>
            <select
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              data-testid="rank-category"
              className="w-full min-h-11 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            >
              {CATEGORIES.map((c) => (
                <option key={c.v} value={c.v}>{c.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-bold text-slate-700 mb-1 inline-block">
              Your marks {exam ? `(of ${exam.out_of})` : ""}
            </span>
            <input
              type="number"
              value={marks}
              onChange={(e) => setMarks(e.target.value)}
              data-testid="rank-marks"
              placeholder="e.g., 138"
              className="w-full min-h-11 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            />
          </label>
        </div>

        {result && (
          <div className={`mt-5 rounded-xl border-2 p-5 ${colourMap[result.tier]}`} data-testid="rank-verdict">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-5 h-5" aria-hidden="true" />
              <span className="text-xs font-bold uppercase tracking-wider">{exam.exam} · {cat.toUpperCase()}</span>
            </div>
            <p className="text-2xl font-extrabold mb-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
              {result.label}
            </p>
            <p className="text-sm leading-relaxed">{result.detail}</p>
          </div>
        )}

        {!valid && marks && (
          <p className="mt-3 text-sm text-amber-700" data-testid="rank-invalid">
            Please enter a valid number between 0 and {exam ? exam.out_of : "—"}.
          </p>
        )}
      </section>

      {exam && (
        <section className="mt-7">
          <h2 className="text-2xl font-extrabold text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            {exam.exam} — Last 3 years cut-off (out of {exam.out_of})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse bg-white" data-testid="rank-cutoff-table">
              <thead>
                <tr className="bg-slate-100">
                  <th scope="col" className="text-left px-3 py-2 font-bold text-slate-700">Year</th>
                  {CATEGORIES.map((c) => (
                    <th key={c.v} scope="col" className="text-right px-3 py-2 font-bold text-slate-700">{c.label.split(" ")[0]}</th>
                  ))}
                  <th scope="col" className="text-left px-3 py-2 font-bold text-slate-700">Notes</th>
                </tr>
              </thead>
              <tbody>
                {exam.rows.map((r) => (
                  <tr key={r.year} className="border-t border-slate-200">
                    <th scope="row" className="text-left px-3 py-2 text-slate-700 font-semibold">{r.year}</th>
                    {CATEGORIES.map((c) => (
                      <td key={c.v} className={`text-right px-3 py-2 ${c.v === cat ? "bg-orange-50 font-bold text-orange-900" : "text-slate-800"}`}>
                        {r[c.v] != null ? r[c.v] : "—"}
                      </td>
                    ))}
                    <td className="text-left px-3 py-2 text-slate-500 text-xs">{r.notes || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Source:{" "}
            <a href={exam.source} target="_blank" rel="noopener noreferrer" className="underline text-orange-600">
              {exam.source}
            </a>
          </p>
        </section>
      )}

      <p className="mt-8 text-xs text-slate-500 leading-relaxed">
        Disclaimer: Predictions are based on historical cut-offs and are indicative only. Final cut-off depends on overall difficulty, normalisation, and vacancy count of the year you appear in.
      </p>
    </div>
  );
}
